import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/api/client';
import { Button } from '../../../core/components/Button';
import { toast } from 'react-hot-toast';
import { ArrowUp, ArrowDown, Trash2, Plus, Eye, EyeOff, Save, Settings, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { SectionConfigDialog } from './components/SectionConfigDialog';
import { useAdminT } from '../../../hooks/useAdminT';

// Section type keys — labels come from locale
const SECTION_TYPE_KEYS = [
    'hero_section',
    'hero_slider',
    'featured_projects',
    'locations_section',
    'featured_units',
    'trust_section',
    'discovery_header',
    'featured_destinations',
    'smart_collections',
    'map_preview',
    'expert_pick',
    'activity_feed',
];

interface Section {
    id: string;
    type: string;
    isVisible: boolean;
    config: any;
}

export const PageBuilderPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useAdminT();
    const queryClient = useQueryClient();

    const [sections, setSections] = useState<Section[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [editingConfigId, setEditingConfigId] = useState<string | null>(null);

    const { data: page, isLoading } = useQuery({
        queryKey: ['admin', 'pages', id],
        queryFn: async () => {
            const res = await api.get<any>(`/api/admin/pages/${id}`);
            return res.data;
        }
    });

    useEffect(() => {
        if (page?.sections) {
            setSections(page.sections);
        }
    }, [page]);

    const updatePageMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/api/admin/pages/${id}`, data),
        onSuccess: () => {
            toast.success(t('pageBuilder.toastSaved'));
            setIsDirty(false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'pages', id] });
            queryClient.invalidateQueries({ queryKey: ['page'] });
        },
        onError: () => {
            toast.error(t('pageBuilder.toastFailed'));
        }
    });

    const handleAddSection = (type: string) => {
        const newSection: Section = { id: `${type}-${Date.now()}`, type, isVisible: true, config: {} };
        setSections([...sections, newSection]);
        setIsDirty(true);
    };

    const handleRemoveSection = (index: number) => {
        if (!window.confirm(t('pageBuilder.confirmRemove'))) return;
        const newSections = [...sections];
        newSections.splice(index, 1);
        setSections(newSections);
        setIsDirty(true);
    };

    const handleMove = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === sections.length - 1) return;
        const newSections = [...sections];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
        setSections(newSections);
        setIsDirty(true);
    };

    const handleToggleVisibility = (index: number) => {
        const newSections = [...sections];
        newSections[index].isVisible = !newSections[index].isVisible;
        setSections(newSections);
        setIsDirty(true);
    };

    const handleSave = () => updatePageMutation.mutate({ sections });

    const handleSaveConfig = (newConfig: any) => {
        if (!editingConfigId) return;
        setSections(sections.map(s => s.id === editingConfigId ? { ...s, config: newConfig } : s));
        setIsDirty(true);
        setEditingConfigId(null);
    };

    // Translate section type key to readable label
    const getSectionLabel = (typeKey: string) => {
        const key = `pageBuilder.sectionTypes.${typeKey}`;
        const translated = t(key);
        // If key not found (returned as-is), fall back to formatted key
        return translated === key ? typeKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : translated;
    };

    if (isLoading) {
        return (
            <div className="p-8 flex items-center justify-center h-64">
                <div className="flex items-center gap-3 text-slate-500">
                    <div className="w-5 h-5 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">{t('common.loading')}</span>
                </div>
            </div>
        );
    }

    const pageTitle = page?.translations?.find((t: any) => t.locale === 'en')?.title
        || page?.translations?.[0]?.title
        || page?.slug
        || '...';

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-secondary mb-1">
                        {t('pageBuilder.title')}: <span className="text-slate-700">{pageTitle}</span>
                    </h1>
                    <p className="text-slate-500 text-sm">{t('pageBuilder.subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="outline" onClick={() => navigate('/admin/cms/pages')}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={!isDirty || updatePageMutation.isPending}>
                        <Save className="w-4 h-4 mr-2" />
                        {updatePageMutation.isPending ? t('common.saving') : t('pageBuilder.save')}
                    </Button>
                </div>
            </div>

            {/* Unsaved changes indicator */}
            {isDirty && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2.5 rounded-xl text-sm mb-6">
                    <AlertCircle size={15} className="shrink-0" />
                    {t('settings.unsavedChanges')}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Available Sections Sidebar */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 sticky top-6">
                        <h3 className="font-bold text-secondary mb-4 text-sm uppercase tracking-wide">
                            {t('pageBuilder.availableSections')}
                        </h3>
                        <div className="space-y-2">
                            {SECTION_TYPE_KEYS.map(typeKey => (
                                <button
                                    key={typeKey}
                                    onClick={() => handleAddSection(typeKey)}
                                    className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group text-left"
                                >
                                    <span className="font-medium text-slate-700 group-hover:text-primary text-sm">
                                        {getSectionLabel(typeKey)}
                                    </span>
                                    <Plus className="w-4 h-4 text-slate-400 group-hover:text-primary shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Active Sections */}
                <div className="lg:col-span-2 space-y-3">
                    {sections.length === 0 ? (
                        <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                            <p className="text-slate-400 text-sm">{t('pageBuilder.noSections')}</p>
                        </div>
                    ) : (
                        sections.map((section, index) => {
                            const label = getSectionLabel(section.type);
                            return (
                                <div
                                    key={section.id}
                                    className={clsx(
                                        "bg-white rounded-xl shadow-sm border p-4 transition-all",
                                        section.isVisible ? "border-slate-200" : "border-slate-200 bg-slate-50 opacity-70"
                                    )}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 flex items-center justify-center bg-secondary/10 rounded-lg text-secondary">
                                                <span className="font-mono text-xs font-bold">{index + 1}</span>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-slate-800 text-sm">{label}</h4>
                                                <p className="text-xs text-slate-400 font-mono">{section.type}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {/* Move Up/Down */}
                                            <div className="flex bg-slate-100 rounded-lg p-0.5 mr-1">
                                                <button
                                                    onClick={() => handleMove(index, 'up')}
                                                    disabled={index === 0}
                                                    className="p-1.5 rounded hover:bg-white text-slate-500 hover:text-secondary disabled:opacity-30 transition-all"
                                                    title={t('pageBuilder.moveUp')}
                                                >
                                                    <ArrowUp className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleMove(index, 'down')}
                                                    disabled={index === sections.length - 1}
                                                    className="p-1.5 rounded hover:bg-white text-slate-500 hover:text-secondary disabled:opacity-30 transition-all"
                                                    title={t('pageBuilder.moveDown')}
                                                >
                                                    <ArrowDown className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Toggle Visibility */}
                                            <button
                                                onClick={() => handleToggleVisibility(index)}
                                                className={clsx(
                                                    "p-2 rounded-lg transition-colors",
                                                    section.isVisible
                                                        ? "text-slate-400 hover:text-secondary hover:bg-slate-100"
                                                        : "text-slate-400 bg-slate-100 hover:text-secondary"
                                                )}
                                                title={section.isVisible ? t('pageBuilder.hide') : t('pageBuilder.show')}
                                            >
                                                {section.isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                            </button>

                                            {/* Configure */}
                                            <button
                                                onClick={() => setEditingConfigId(section.id)}
                                                className="p-2 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                                                title={t('pageBuilder.configure')}
                                            >
                                                <Settings className="w-4 h-4" />
                                            </button>

                                            {/* Remove */}
                                            <button
                                                onClick={() => handleRemoveSection(index)}
                                                className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                title={t('pageBuilder.remove')}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Section Config Dialog */}
            <SectionConfigDialog
                isOpen={!!editingConfigId}
                onClose={() => setEditingConfigId(null)}
                section={sections.find(s => s.id === editingConfigId) || {}}
                onSave={handleSaveConfig}
            />
        </div>
    );
};
